import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi, apiError, money } from "@/lib/api";
import { StatusBadge, inputCls, fmt12 } from "./AdminUI";

const ACTIONS = {
  pending: [["approve", "Approve"], ["reject", "Reject"], ["cancel", "Cancel"]],
  confirmed: [["complete", "Mark completed"], ["cancel", "Cancel"]],
};

const Info = ({ label, value }) => (
  <div><p className="text-xs uppercase tracking-wide text-slate-400">{label}</p><p className="text-sm text-[#0B132B]">{value || "-"}</p></div>
);

const Reschedule = ({ b, notify, onDone }) => {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");

  const pick = async (d) => {
    setDate(d);
    setTime("");
    const r = await adminApi.get("/admin/inspections/slots", { params: { service_id: b.service_id, date: d, exclude: b.reference } });
    setSlots(r.data.slots);
  };
  const save = async () => {
    try {
      onDone((await adminApi.post(`/admin/inspections/bookings/${b.reference}/reschedule`, { date, time, notify })).data);
      toast.success("Rescheduled");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-sm font-semibold text-[#0B132B]">Reschedule</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input type="date" className={`${inputCls} w-44`} value={date} onChange={(e) => pick(e.target.value)} data-testid="reschedule-date" />
        <select className={`${inputCls} w-36`} value={time} onChange={(e) => setTime(e.target.value)} data-testid="reschedule-time">
          <option value="">{date ? (slots.length ? "Time…" : "No times") : "Pick date"}</option>
          {slots.map((s) => <option key={s} value={s}>{fmt12(s)}</option>)}
        </select>
        <button type="button" disabled={!time} onClick={save} data-testid="reschedule-save"
          className="rounded-full bg-gold px-4 py-2 text-sm font-semibold text-[#0B132B] disabled:opacity-50">Save new time</button>
      </div>
    </div>
  );
};

export const BookingDialog = ({ booking: b, autoNotify, onClose, onUpdated }) => {
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(autoNotify);
  if (!b) return null;

  const act = async (action) => {
    try {
      const r = await adminApi.post(`/admin/inspections/bookings/${b.reference}/action`, { action, note, notify });
      onUpdated(r.data);
      setNote("");
      toast.success(`Booking ${r.data.status}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            <span className="font-mono-x">{b.reference}</span> <StatusBadge status={b.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Info label="Service" value={b.service_name} />
          <Info label="Appointment" value={`${b.date} at ${fmt12(b.time)} (${b.duration_minutes} min)`} />
          <Info label="Customer" value={`${b.full_name} · ${b.customer_type}`} />
          <Info label="Contact" value={<><a className="underline" href={`mailto:${b.email}`}>{b.email}</a><br /><a className="underline" href={`tel:${b.phone}`}>{b.phone}</a></>} />
          <Info label="Property" value={`${b.address}, ${b.city}, ${b.state} ${b.zip}`} />
          <Info label="Details" value={`${b.property_type} · ${b.sqft} sq ft · ${b.units} unit(s) · ${b.occupancy}${b.year_built ? ` · built ${b.year_built}` : ""}`} />
          <Info label="Access instructions" value={b.access_instructions} />
          <Info label="Notes" value={b.notes} />
        </div>
        <div className="rounded-xl bg-[#FAF8F1] p-4 text-sm">
          {b.quote.items.map((i) => <div key={i.label} className="flex justify-between"><span>{i.label}</span><span>{money(i.amount)}</span></div>)}
          <div className="mt-2 flex justify-between border-t pt-2 font-semibold"><span>Estimated total</span><span data-testid="dialog-total">{money(b.quote.total)}</span></div>
        </div>
        {ACTIONS[b.status] && (
          <div className="space-y-3">
            <input className={inputCls} placeholder="Internal note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="action-note" />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="action-notify" /> Email the customer about this change
            </label>
            <div className="flex flex-wrap gap-2">
              {ACTIONS[b.status].map(([a, label]) => (
                <button key={a} type="button" onClick={() => act(a)} data-testid={`action-${a}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${a === "approve" || a === "complete" ? "bg-[#0B132B] text-white" : "border border-slate-300 text-slate-700"}`}>{label}</button>
              ))}
            </div>
            <Reschedule b={b} notify={notify} onDone={onUpdated} />
          </div>
        )}
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">History</p>
          <ul className="mt-2 space-y-1 text-xs text-slate-600" data-testid="booking-history">
            {b.history.map((h, i) => <li key={i}>{new Date(h.at).toLocaleString()} · <b>{h.action}</b>{h.note ? ` · ${h.note}` : ""}{h.by ? ` · ${h.by}` : ""}</li>)}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};
